import { useCallback, useMemo } from 'react';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { ActionType, ComponentType } from '@cbhq/client-analytics';

import { useAnalytics } from '../hooks/useAnalytics';
import { useNavigate } from '../hooks/useNavigate';
import { useSessionDelete } from '../hooks/useSession';
import { useProfileMenuV2 } from '../providers/ProfileMenuV2Provider';
import { MenuActionItem } from '../types';

import { ActionItem as UserSwitcherActionItem } from './UserSwitcher/ActionItem';
import { SignOutAllAccountsButton } from './UserSwitcher/SignOutAllAccountsButton';
import { ActionItem } from './ActionItem';
import { FallbackIcon } from './FallbackIcon';

type ProfileMenuActionItemProps = {
  apiAction: MenuActionItem;
};

export function ProfileMenuActionItem({ apiAction }: ProfileMenuActionItemProps): JSX.Element {
  const { logEvent } = useAnalytics();
  const { userSwitcherEnabled } = useProfileMenuV2();

  const navigate = useNavigate();
  const deleteSession = useSessionDelete();
  const menuItemPressHandler = useCallback(async () => {
    logEvent('profile_menu_action', {
      action: ActionType.click,
      componentType: ComponentType.dropdown,
      actionKey: apiAction.key,
      userSwitcherEnabled,
    });
    if (apiAction.key === 'sign_out' && !userSwitcherEnabled) {
      await deleteSession();
    }
    await navigate(apiAction.callbackUrl, apiAction.key);
  }, [
    apiAction.callbackUrl,
    apiAction.key,
    userSwitcherEnabled,
    logEvent,
    navigate,
    deleteSession,
  ]);

  const icon = useMemo(() => {
    if (apiAction.key === 'sign_out') {
      return <Icon name="backArrow" size="s" color="fgNegative" paddingX={0.5} />;
    }
    if (!apiAction.icon) {
      return <FallbackIcon />;
    }
    return <Icon name={apiAction.icon} size="m" fallback={<FallbackIcon />} color="fg" />;
  }, [apiAction.icon, apiAction.key]);

  if (userSwitcherEnabled) {
    if (apiAction.key === 'sign_out')
      return <SignOutAllAccountsButton label={apiAction.label} onPress={menuItemPressHandler} />;

    return (
      <UserSwitcherActionItem
        label={apiAction.label}
        icon={icon}
        onPress={menuItemPressHandler}
        testID={`profile-menu-action-item-${apiAction.key}`}
      />
    );
  }

  return (
    <ActionItem
      label={apiAction.label}
      icon={icon}
      negative={apiAction.key === 'sign_out'}
      onPress={menuItemPressHandler}
      testID={`profile-menu-action-item-${apiAction.key}`}
    />
  );
}
